import { useEffect, useRef } from "react";

/**
 * Ascension reveal v2.
 *
 * Same API as before — attach the returned ref to any element — but the
 * element now rises out of a soft blur instead of just sliding up,
 * driven by the .reveal-asc / .is-in classes in global.css.
 *
 * Optional stagger: set data-reveal-delay="120" (ms) on the element.
 * Honors prefers-reduced-motion (elements simply appear).
 */
export function useReveal<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    el.classList.add("reveal-asc");
    const delay = el.dataset.revealDelay;
    if (delay) el.style.transitionDelay = `${parseInt(delay, 10)}ms`;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}
